import { JSX, useEffect, useRef, useState } from "react"
import { useParams, useNavigate, Navigate } from "react-router-dom"
import { Box, Drawer, HStack, Portal, Spinner, Stack } from "@chakra-ui/react"

import { DealManagerNavBar, DealManagerNavigation, ViewAsNavBar, ViewAsNavigation } from "@/components"
import { useAuth, useViewer } from "@/contexts"
import { useIsMobile } from "@/hooks"
import { Deal, NavOption } from "@/types"

import { ClientsPage, CompliancePage, DealsPage, DealsTabPage, DMDashboardPage, DocumentsPage, PaymentCenterPage, ResourcesPage, TaxesPage } from "./"
import { useDealManagerClients, useDealManagerDeals, useValidatedClientDeals } from "@/hooks"
import { getClientById } from "@/api"

interface Props {
  currentNavOption: string
}

export const DealManagerDashboardPage = (props: Props) => {
  const { user } = useAuth()
  const { data: clients, isLoading } = useDealManagerClients()
  const { data: dealManagerDeals, isLoading: isLoadingDeals } = useDealManagerDeals()
  const { viewedClient, setViewedClient } = useViewer()
  const { clientId } = useParams()
  const navigate = useNavigate()
  const sectionRef = useRef<HTMLDivElement>(null)
  const [currentClientTab, setCurrentClientTab] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Fetch validated deals when viewing a specific client
  const viewedClientId = props.currentNavOption === NavOption.CLIENT ? viewedClient?.id ?? null : null
  const { data: validatedDeals, isLoading: isValidatingDeals } = useValidatedClientDeals(viewedClientId)

  const [currentDeal, setCurrentDeal] = useState<Deal | null>(null)

  // Default to Deals tab if client has deals, otherwise Documents
  useEffect(() => {
    if (props.currentNavOption === NavOption.CLIENT && !isValidatingDeals && currentClientTab === null) {
      const deals = validatedDeals.length > 0 ? validatedDeals : viewedClient?.deals || []
      setCurrentClientTab(deals.length > 0 ? NavOption.CURRENT_OFFER : NavOption.DOCUMENTS)
    }
  }, [props.currentNavOption, isValidatingDeals, validatedDeals, viewedClient])

  // Default currentDeal to most recent deal when deals load
  useEffect(() => {
    const deals = validatedDeals.length > 0 ? validatedDeals : viewedClient?.deals || []
    if (deals.length > 0 && !currentDeal) {
      const mostRecent = deals.reduce((latest, deal) =>
        deal.id > latest.id ? deal : latest, deals[0]
      )
      setCurrentDeal(mostRecent)
    }
  }, [validatedDeals, viewedClient])

  // Keep currentDeal in sync when deals data refreshes
  useEffect(() => {
    const deals = validatedDeals.length > 0 ? validatedDeals : viewedClient?.deals || []
    if (currentDeal && deals.length > 0) {
      const updated = deals.find(d => d.id === currentDeal.id)
      if (updated && updated !== currentDeal) {
        setCurrentDeal(updated)
      }
    }
  }, [validatedDeals, viewedClient])

  // When navigating to /clients/:clientId, find and set the viewed client.
  // First tries the DM's pre-loaded client list; if the client isn't there
  // (cross-DM impersonation via ESO Core), fetches the client directly.
  useEffect(() => {
    if (props.currentNavOption === NavOption.CLIENT && clientId) {
      const found = clients.find(c => String(c.id) === clientId)
      if (found) {
        setViewedClient(found)
        return
      }
      // Fallback: client not in DM's list. Wait for list to finish loading
      // to avoid racing, then fetch the single client directly.
      if (!isLoading && !viewedClient) {
        getClientById(Number(clientId))
          .then(client => client ? setViewedClient(client) : navigate("/eso/deals"))
          .catch(() => navigate("/eso/deals"))
      }
    }

    return () => {
      if (props.currentNavOption === NavOption.CLIENT) {
        setViewedClient(null)
        setCurrentClientTab(null)
        setCurrentDeal(null)
      }
    }
  }, [props.currentNavOption, clientId, clients, isLoading])

  // Scroll to top on tab change
  useEffect(() => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "instant" })
  }, [props.currentNavOption, currentClientTab])

  const handleExitViewAs = () => {
    setViewedClient(null)
    setCurrentClientTab(null)
    navigate("/eso/deals")
  }

  if (!user) {
    return <div>Loading...</div>
  }

  // A client can reach the deal-manager routes (/dashboard, /clients,
  // /clients/:id, /eso/deals) because ProtectedRoute only checks that someone is
  // logged in, not what they are. Send them home rather than showing them a
  // staff message about an unassigned equity partner, which is not their
  // situation and reads as "your account is broken".
  if (user.userType === "client") {
    return <Navigate to="/deals" replace />
  }

  // Message, not a redirect — see the matching note in DashboardPage.
  if (user.userType !== "deal_manager") {
    return <div>Please contact support. Your account is not set up for this area.</div>
  }

  // View-as mode: render client dashboard for the selected client
  if (props.currentNavOption === NavOption.CLIENT) {
    if (!viewedClient) {
      return (
        <HStack w="100%" h="100vh" justifyContent="center" alignItems="center">
          <Spinner />
        </HStack>
      )
    }

    const deals = validatedDeals.length > 0 ? validatedDeals : viewedClient.deals || []
    const clientName = `${viewedClient.firstName} ${viewedClient.lastName}`

    const hasActiveDeal = deals.some(d => d.isActive === 1)
    const effectiveClientTab = currentClientTab === NavOption.PAYMENT_CENTER && !hasActiveDeal
      ? NavOption.DOCUMENTS
      : currentClientTab

    const clientComponentMap: Record<string, JSX.Element> = {
      [NavOption.DOCUMENTS]: <DocumentsPage deals={deals} isLoading={isValidatingDeals} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
      [NavOption.CURRENT_OFFER]: <DealsPage deals={deals} isLoading={isValidatingDeals} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
      [NavOption.RESOURCES]: <ResourcesPage />,
      [NavOption.TAX_CALCULATION]: <TaxesPage />,
      [NavOption.COMPLIANCE]: <CompliancePage deals={deals} isLoading={isValidatingDeals} onNavigateToDocuments={() => setCurrentClientTab(NavOption.DOCUMENTS)} currentDeal={currentDeal} onDealChange={setCurrentDeal} />,
      [NavOption.PAYMENT_CENTER]: <PaymentCenterPage deals={deals} isLoading={isValidatingDeals} />,
    }

    return (
      <HStack w="100%" h="100vh" gap={0}>
        {!isMobile && (
          <Box w="20vw" h="100%">
            <ViewAsNavigation
              deals={deals}
              dealManager={viewedClient.dealManager}
              currentTab={currentClientTab}
              onTabChange={setCurrentClientTab}
              clientName={clientName}
              clientEmail={viewedClient.email}
              onBack={handleExitViewAs}
            />
          </Box>
        )}
        <Stack w={isMobile ? "100%" : "80vw"} h="100%">
          <ViewAsNavBar
            deals={deals}
            currentDeal={currentDeal}
            onDealChange={setCurrentDeal}
            onOpenDrawer={isMobile ? () => setIsDrawerOpen(true) : undefined}
          />
          <Stack ref={sectionRef} bg="white" w="100%" h="100%" overflowY="auto">
            {effectiveClientTab ? (clientComponentMap[effectiveClientTab] || <div>Tab not found</div>) : <HStack w="100%" h="100%" justifyContent="center" alignItems="center"><Spinner /></HStack>}
          </Stack>
        </Stack>

        {isMobile && (
          <Drawer.Root
            open={isDrawerOpen}
            onOpenChange={(e) => setIsDrawerOpen(e.open)}
            placement="start"
          >
            <Portal>
              <Drawer.Backdrop />
              <Drawer.Positioner>
                <Drawer.Content bg="darkBlue.200" maxW="280px">
                  <Drawer.Body p={0}>
                    <ViewAsNavigation
                      deals={deals}
                      dealManager={viewedClient.dealManager}
                      currentTab={currentClientTab}
                      onTabChange={setCurrentClientTab}
                      clientName={clientName}
                      clientEmail={viewedClient.email}
                      onBack={handleExitViewAs}
                      onNavItemClick={() => setIsDrawerOpen(false)}
                    />
                  </Drawer.Body>
                </Drawer.Content>
              </Drawer.Positioner>
            </Portal>
          </Drawer.Root>
        )}
      </HStack>
    )
  }

  // Normal DM dashboard mode
  const componentMap: Record<string, JSX.Element> = {
    [NavOption.DASHBOARD]: <DMDashboardPage />,
    [NavOption.CLIENTS]: <ClientsPage clients={clients} isLoading={isLoading} />,
    [NavOption.DEALS]: <DealsTabPage deals={dealManagerDeals} isLoading={isLoadingDeals} />,
  }

  const selectedComponent = componentMap[props.currentNavOption] || <div>Component not found</div>

  return (
    <HStack w="100%" h="100vh" gap={0}>
      {!isMobile && (
        <Box w="20vw" h="100%">
          <DealManagerNavigation />
        </Box>
      )}
      <Stack w={isMobile ? "100%" : "80vw"} h="100%">
        <DealManagerNavBar
          deals={dealManagerDeals}
          onOpenDrawer={isMobile ? () => setIsDrawerOpen(true) : undefined}
        />
        <Stack ref={sectionRef} bg="white" w="100%" h="100%" overflowY="auto">
          {selectedComponent}
        </Stack>
      </Stack>

      {isMobile && (
        <Drawer.Root
          open={isDrawerOpen}
          onOpenChange={(e) => setIsDrawerOpen(e.open)}
          placement="start"
        >
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content bg="darkBlue.200" maxW="280px">
                <Drawer.Body p={0}>
                  <DealManagerNavigation onNavItemClick={() => setIsDrawerOpen(false)} />
                </Drawer.Body>
              </Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </Drawer.Root>
      )}
    </HStack>
  )
}
